import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useContent } from "../../state/siteContent";
import { openLeadModal } from "../LeadModal";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useContent();

  // EVERY page now opens with a dark hero (homepage = full-screen video,
  // inner pages = compact image banner), so the header starts transparent
  // with light text on all of them, then becomes the solid green bar once
  // the user scrolls past that hero.
  //
  // The flip point differs per page because the heroes differ in height:
  // the homepage hero is ~100svh; inner-page heroes are a compact banner
  // (clamp(280px, 38vh, 400px)), so we measure the actual hero element
  // instead of assuming a viewport multiple.
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      setScrolled(y > 24);

      // Measure the real hero on this page; fall back to one viewport.
      const hero =
        document.querySelector(".hv-hero") ||
        document.querySelector(".page-hero");
      const heroBottom = hero
        ? hero.getBoundingClientRect().height
        : window.innerHeight;
      // flip slightly before the hero fully leaves, so the bar is solid
      // by the time content scrolls under it
      setPastHero(y > heroBottom - 90);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Utility bar — social links + contacts. Desktop only, and fades out
          once scrolled past the hero so it never competes with the floating
          pill for attention on inner content. (RERA registration number
          removed site-wide per client request.) Social icons on the left
          balance the contacts on the right instead of leaving the bar's
          edges empty around a single centered block. */}
      <div className={`site-util ${scrolled ? "is-hidden" : ""}`} aria-hidden={scrolled}>
        <div className="site-util__inner">
          <div className="site-util__social" aria-label="Social media">
            {settings.instagram && (
              <a href={settings.instagram} target="_blank" rel="noreferrer" className="site-util__social-link" aria-label="Instagram" title="Instagram">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="1.7" />
                  <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.7" />
                  <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" />
                </svg>
              </a>
            )}
            {settings.linkedin && (
              <a href={settings.linkedin} target="_blank" rel="noreferrer" className="site-util__social-link" aria-label="LinkedIn" title="LinkedIn">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-.95 1.83-1.95 3.77-1.95C20.6 8.75 22 10.9 22 14.1V21h-4v-6.1c0-1.45-.03-3.32-2.02-3.32-2.02 0-2.33 1.58-2.33 3.21V21H9z" />
                </svg>
              </a>
            )}
            {settings.facebook && (
              <a href={settings.facebook} target="_blank" rel="noreferrer" className="site-util__social-link" aria-label="Facebook" title="Facebook">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                  <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H17V3.6c-.29-.04-1.27-.12-2.41-.12-2.39 0-4.02 1.46-4.02 4.13V9.9H7.8V13h2.77v8z" />
                </svg>
              </a>
            )}
          </div>
          <span className="site-util__contacts">
            <span className="site-util__link">{settings.hrPhone || "+91 70090 05400"}</span>
            <span className="site-util__sep" aria-hidden="true" />
            <a href={`mailto:${settings.email}`} className="site-util__link">{settings.email}</a>
          </span>
        </div>
      </div>

      <header
        className={`site-nav ${scrolled ? "is-scrolled" : ""} ${
          !pastHero ? "is-transparent" : ""
        }`}
      >
        <Link to="/" className="site-nav__brand" aria-label={`${settings.brandName} - ${settings.brandTagline}`}>
          <span className="brand-chip">
            <img
              src={settings.logoMark}
              alt={settings.brandName}
              className="site-nav__brand-logo"
              width="209"
              height="68"
              decoding="async"
            />
          </span>
        </Link>

        <nav className="site-nav__links" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `site-nav__link ${isActive ? "is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
          <button type="button" className="site-nav__cta" onClick={openLeadModal}>
            Reserve a Plot
          </button>
        </nav>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className={`site-nav__menu-btn ${open ? "is-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
        </button>
      </header>

      <div className={`site-nav__mobile ${open ? "is-open" : ""}`} aria-hidden={!open}>
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) => (isActive ? "is-active" : "")}
          >
            {l.label}
          </NavLink>
        ))}
        <button
          type="button"
          className="site-nav__mobile-cta"
          onClick={() => { setOpen(false); openLeadModal(); }}
        >
          Reserve a Plot
        </button>

        {/* Fallback contact — always reachable from the menu without having
            to navigate to the Contact page first. */}
        <div className="site-nav__mobile-contact">
          <a href={`tel:${settings.phoneRaw}`}>{settings.phone}</a>
          <span>{settings.office?.cityLabel}</span>
        </div>
      </div>
    </>
  );
}
